/**
 * Cálculo del monto reembolsable esperado para un claim item.
 *
 * Regla de negocio (alineada con el legacy de mascotaysalud):
 *
 *   reimbursable = min(
 *     amountClp * coverage.coveragePercentage,
 *     coverage.capUfPerEvent * ufValue
 *   )
 *
 * - Si no hay coverage configurada → 0 (sin cobertura, no reembolsa).
 * - Si no hay cap → solo aplica el porcentaje.
 * - Si no hay porcentaje → 0.
 * - El cap está en UF, el monto en CLP: convertimos UF→CLP con el valor del
 *   día (`uf_value_today` del plan detail).
 *
 * Devuelve además flags para que el frontend muestre por qué un monto se
 * vio recortado (útil para explicar al usuario).
 */

import type { PlanDetailResponse } from '@modules/plans/application/dto/PlanDTO';

export interface ReimbursementBreakdown {
  /** Monto que reembolsaría el plan si se aprueba como está. */
  estimatedReimbursableClp: number;
  /** Porcentaje de cobertura aplicado (0..1). */
  coveragePercentage: number;
  /** Tope por evento en CLP (calculado desde UF). Null si no hay tope. */
  capPerEventClp: number | null;
  /** Tope anual en CLP. Null si no aplica. */
  capAnnualClp?: number | null;
  /** ¿El reembolso quedó topeado por el cap por evento? */
  cappedByEvent: boolean;
  /** Monto sin tope (porcentaje × solicitado). */
  beforeCapClp: number;
  /** Es true si no hay coverage configurada o el porcentaje es 0. */
  noCoverage: boolean;
}

export interface CalcInput {
  amountClp: number;
  coverageTypeId: string;
  plan: PlanDetailResponse | undefined;
}

/**
 * Busca la coverage del plan que matchea el coverageTypeId del item.
 * El plan detail trae `coverages[].id` que en realidad mapea al `coverage_type_id`
 * de claim_items (cada coverage del plan apunta a un coverage_type específico).
 *
 * Importante: en el backend `coverages.id` ≠ `coverages.coverage_type_id`.
 * La tabla `claim_items.coverage_type_id` referencia `coverages_type.id`, no
 * `coverages.id`. Pero el `PlanDetailResponse.coverages[].id` que recibimos
 * en frontend trae el id de la fila de `coverages` (con sus % y cap), y el
 * wizard nuevo (NuevaBonificacionPage) guarda ese mismo id en `coverageTypeId`
 * del item al crear el claim. Por eso comparamos por id directo.
 */
function findCoverage(
  coverageTypeId: string,
  plan: PlanDetailResponse | undefined,
) {
  if (!plan?.coverages) return undefined;
  return plan.coverages.find((c) => c.id === coverageTypeId);
}

export function calculateReimbursement(input: CalcInput): ReimbursementBreakdown {
  const { amountClp, coverageTypeId, plan } = input;
  const coverage = findCoverage(coverageTypeId, plan);
  const ufValue = plan?.uf_value_today ?? 0;

  if (!coverage || !coverage.coverage_percentage) {
    return {
      estimatedReimbursableClp: 0,
      coveragePercentage: 0,
      capPerEventClp: null,
      cappedByEvent: false,
      beforeCapClp: 0,
      noCoverage: true,
    };
  }

  const beforeCap = amountClp * coverage.coverage_percentage;
  const capClp =
    coverage.cap_uf_per_event != null && ufValue > 0
      ? coverage.cap_uf_per_event * ufValue
      : null;

  const reimbursable = capClp != null ? Math.min(beforeCap, capClp) : beforeCap;
  const cappedByEvent = capClp != null && beforeCap > capClp;

  return {
    estimatedReimbursableClp: Math.round(reimbursable),
    coveragePercentage: coverage.coverage_percentage,
    capPerEventClp: capClp != null ? Math.round(capClp) : null,
    cappedByEvent,
    beforeCapClp: Math.round(beforeCap),
    noCoverage: false,
  };
}

/** Devuelve el nombre legible de la coverage (para usar en UI). */
export function coverageNameFromPlan(
  coverageTypeId: string,
  plan: PlanDetailResponse | undefined,
): string | undefined {
  return findCoverage(coverageTypeId, plan)?.name;
}
